import AddCircleOutlineOutlined from "@mui/icons-material/AddCircleOutlineOutlined";
import { Box, Button, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router";
import { config } from "../../../../config";
import { useToken } from "../../../../server/auth/use-token";
import { useSupabase } from "../../../../server/supabase/hooks";
import { SlackIcon } from "../../../../styles/icons/slack";
import { authHeader } from "../../../../utils/auth-header";
import { LocalStorageService } from "../../../../utils/local-storage";
import { useLogo } from "../../../layout/components/sidebar/hooks/use-logo";
import { slackAdmin } from "../../../slack-admin/slack-admin";

import type { UUID } from "../../../../utils/uuid";
import type { SlackAppConfig } from "../types";
import type { FC } from "react";

const STORAGE_KEY = "slackState";

export const SlackCardV2: FC<{ slackApp: SlackAppConfig; reload: () => void }> = ({
  slackApp,
  reload,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const getToken = useToken();

  useSupabase(async () => {
    const state = searchParams.get("state");
    const code = searchParams.get("code");

    const localStorage = new LocalStorageService();
    const storedSlackAppId = localStorage.get(STORAGE_KEY);

    if (!state || !code || storedSlackAppId !== state || slackApp.id !== state) return;

    setLoading(true);

    const response = await slackAdmin.POST("/connection", {
      body: {
        code,
        slack_app_id: state,
        redirect_uri: `${globalThis.location.origin}/channels/slack`,
      },
      ...authHeader(await getToken()),
    });

    if (response.error) {
      const errBody = response.error as { error?: string };
      toast.error(errBody.error ?? "Failed to connect to Slack workspace");
      return;
    } else {
      toast.success("Slack Workspace connected successfully");
      reload();
    }

    setLoading(false);
    setSearchParams({});
  }, [getToken, reload, searchParams, setSearchParams, slackApp.id]);

  return slackApp.slack_config_id ? (
    <ConnectedCard
      slackApp={slackApp}
      reload={reload}
      updateLoading={loading}
      setUpdateLoading={setLoading}
    />
  ) : (
    <DisconnectedCard slackApp={slackApp} loading={loading} />
  );
};

const ConnectedCard: FC<{
  slackApp: SlackAppConfig;
  reload: () => void;
  updateLoading: boolean;
  setUpdateLoading: (loading: boolean) => void;
}> = ({ slackApp, reload, updateLoading, setUpdateLoading }) => {
  const { data } = useLogo();
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const getToken = useToken();

  const handleDisconnect = useCallback(async () => {
    setDisconnectLoading(true);

    const response = await slackAdmin.DELETE("/connection/{config_id}", {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      params: { path: { config_id: slackApp.slack_config_id as UUID } },
      ...authHeader(await getToken()),
    });

    if (response.error) {
      const errBody = response.error as { error?: string };
      toast.error(errBody.error ?? "Failed to disconnect from Slack workspace");
    } else {
      toast.success("Slack Workspace disconnected successfully");
      reload();
    }

    setDisconnectLoading(false);
  }, [getToken, reload, slackApp.slack_config_id]);

  const handleUpdate = useCallback(async () => {
    setUpdateLoading(true);

    const response = await slackAdmin.POST("/update-app", {
      body: { slack_app_id: slackApp.id },
      ...authHeader(await getToken()),
    });

    if (response.error) {
      const errBody = response.error as { error?: string };
      toast.error(errBody.error ?? "Failed to update Slack app");
    } else {
      const localStorage = new LocalStorageService();

      const params = new URLSearchParams({
        scope: config.slack.bot_scopes_v5.join(","),
        user_scope: config.slack.user_scopes_v2.join(","),
        redirect_uri: `${globalThis.location.origin}/channels/slack`,
        client_id: slackApp.client_id,
        state: slackApp.id,
      });
      const url = `https://slack.com/oauth/v2/authorize?${params.toString()}`;
      localStorage.set(STORAGE_KEY, slackApp.id);
      globalThis.location.href = url;
    }

    setUpdateLoading(false);
  }, [getToken, setUpdateLoading, slackApp.client_id, slackApp.id]);

  return (
    <Stack
      sx={{
        width: "238px",
        height: "128px",
        backgroundColor: "background.paper",
        display: "flex",
        justifyContent: "flex-start",
        borderRadius: 0.5,
        boxShadow: "1px 0px 6px 0px #64748B1F",
        p: 2,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 2 }}>
        <TitleBar slackApp={slackApp} />
      </Stack>
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            height: 32,
            width: 32,
            flexBasis: 32,
            flexShrink: 0,
          }}
        >
          {slackApp.workspace_icon_url ? (
            <img
              alt="logo"
              src={slackApp.workspace_icon_url}
              style={{
                objectFit: "contain",
                height: "100%",
                width: "100%",
              }}
            />
          ) : (
            <img
              alt="logo"
              src={data?.logo}
              style={{
                objectFit: "contain",
                height: "100%",
                width: "100%",
              }}
            />
          )}
        </Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: 400, fontSize: "14px", color: "text.secondary" }}
        >
          {slackApp.workspace_name || "Your Workspace"}
        </Typography>
      </Stack>
      <Stack direction="row" sx={{ gap: 2, justifyContent: "flex-start", pt: 0.5 }}>
        <Button
          variant="text"
          size="small"
          color="error"
          sx={{ alignSelf: "flex-start", p: 0 }}
          data-analytics-id="slack-disconnect-button"
          disabled={disconnectLoading || updateLoading}
          onClick={() => void handleDisconnect()}
        >
          {disconnectLoading ? "Disconnecting..." : "Disconnect"}
        </Button>
        {slackApp.version !== "5" && (
          <Tooltip title="Update your Slack Integration to enable embedded video" arrow>
            <Button
              variant="text"
              size="small"
              color="secondary"
              sx={{ alignSelf: "flex-start", p: 0 }}
              data-analytics-id="slack-update-connector-button"
              disabled={disconnectLoading || updateLoading}
              onClick={() => void handleUpdate()}
            >
              {updateLoading ? "Updating..." : "Update connector"}
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

const DisconnectedCard: FC<{ slackApp: SlackAppConfig; loading: boolean }> = ({
  slackApp,
  loading,
}) => {
  const initiateConnect = useCallback(() => {
    const localStorage = new LocalStorageService();

    const params = new URLSearchParams({
      scope: config.slack.bot_scopes_v5.join(","),
      user_scope: config.slack.user_scopes_v2.join(","),
      redirect_uri: `${globalThis.location.origin}/channels/slack`,
      client_id: slackApp.client_id,
      state: slackApp.id,
    });
    const url = `https://slack.com/oauth/v2/authorize?${params.toString()}`;
    localStorage.set(STORAGE_KEY, slackApp.id);
    globalThis.location.href = url;
  }, [slackApp.client_id, slackApp.id]);

  return (
    <Stack
      sx={{
        width: "238px",
        height: "128px",
        backgroundColor: "background.default",
        display: "flex",
        justifyContent: "flex-start",
        borderRadius: 0.5,
        border: "1px dashed #D1D5DB",
        p: 2,
      }}
    >
      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mb: 2 }}>
        <TitleBar slackApp={slackApp} />
      </Stack>
      <Button
        variant="text"
        size="small"
        color="primary"
        disabled={loading}
        sx={{ alignSelf: "flex-start", p: 0 }}
        data-analytics-id="slack-connect-button"
        startIcon={
          loading ? (
            <CircularProgress size={16} />
          ) : (
            <AddCircleOutlineOutlined sx={{ width: "16px", height: "16px" }} />
          )
        }
        onClick={() => initiateConnect()}
      >
        {loading ? "Connecting..." : "Connect to a workspace"}
      </Button>
    </Stack>
  );
};

const TitleBar: FC<{ slackApp: SlackAppConfig }> = ({ slackApp }) => (
  <>
    <SlackIcon sx={{ width: "24px", height: "24px" }} />
    <Typography variant="subtitle2" sx={{ fontWeight: 500, fontSize: "14px" }}>
      {slackApp.slack_app_name || "Slack"}
    </Typography>
  </>
);
